import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaQuestionCircle } from 'react-icons/fa';

import { TotemSelect } from 'components/TotemSelect';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { ShelfGrid } from './ShelfGrid';
import { LegendPanel } from './LegendPanel';
import { MicrostoreColumnTemplateFormValues, MicrostoreLocationTemplateFormValues } from './FormHelper/DataMapper';

export const ColumnShelfLocationModernEditor = ({
    microstoreColumnTemplateFormValues,
    setMicrostoreColumnTemplateFormValuesAndIsFormDirty,
    selectedShelfTemplateIndex,
    setSelectedShelfTemplateIndex,
    selectedLocationTemplatePosition,
    setSelectedLocationTemplatePosition,
}: {
    microstoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
    setMicrostoreColumnTemplateFormValuesAndIsFormDirty: ({
        newMicrostoreColumnTemplateFormValues,
        newIsDirty,
    }: {
        newMicrostoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
        newIsDirty?: boolean | undefined;
    }) => void;
    selectedShelfTemplateIndex: number | null;
    setSelectedShelfTemplateIndex: (value: React.SetStateAction<number | null>) => void;
    selectedLocationTemplatePosition: {
        column: number;
        row: number;
    } | null;
    setSelectedLocationTemplatePosition: (
        value: React.SetStateAction<{
            column: number;
            row: number;
        } | null>,
    ) => void;
}) => {
    const [isLegendOpen, setIsLegendOpen] = useState(false);

    // Validation des champs requis avant de changer de location
    function validateCurrentLocationRequiredFields(): boolean {
        if (!selectedShelfTemplateIndex || !selectedLocationTemplatePosition) return true;

        const shelfTemplate = microstoreColumnTemplateFormValues.shelfTemplates[selectedShelfTemplateIndex];
        if (!shelfTemplate?.locationTemplateTable) return true;

        const { row, column } = selectedLocationTemplatePosition;
        const locationTable = shelfTemplate.locationTemplateTable;

        if (row >= locationTable.length || column >= locationTable[row]?.length) return true;

        const location = locationTable[row][column];

        // Valider seulement si produit sans arrangement
        if (!location.product || location.productArrangement) return true;

        const isStockReassortmentType =
            location.reassortmentType === 'Manual' || location.reassortmentType === 'Target';

        const missingFields = [
            !location.reassortmentType && 'Type de réassort',
            isStockReassortmentType && location.stockMax === null && 'Stock max',
            location.reassortmentType === 'Tray' &&
                (!location.trayMaxCapacity || location.trayMaxCapacity <= 0) &&
                'Capacité max',
        ].filter(Boolean);

        if (missingFields.length > 0) {
            toast.error(
                `Veuillez remplir les champs requis avant de changer d'emplacement: ${missingFields.join(', ')}`,
            );
            return false;
        }

        return true;
    }

    function setShelvesOfColumn(numberOfShelves: number) {
        setSelectedShelfTemplateIndex(null);
        setSelectedLocationTemplatePosition(null);
        const newShelfTemplates = [];
        for (let i = 0; i < numberOfShelves; i++) {
            newShelfTemplates.push({ _id: null, locationTemplateTable: [] });
        }
        setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
            newMicrostoreColumnTemplateFormValues: {
                ...microstoreColumnTemplateFormValues,
                shelfTemplates: newShelfTemplates,
            },
        });
    }

    function setLocationsOfShelf(shelfIndex: number, numberOfColumn: number, numberOfRow: number) {
        setSelectedLocationTemplatePosition(null);
        const newLocationTemplateTable: MicrostoreLocationTemplateFormValues[][] = [];

        for (let rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
            const newLocationTemplateRow: MicrostoreLocationTemplateFormValues[] = [];
            for (let columnIndex = 0; columnIndex < numberOfColumn; columnIndex++) {
                newLocationTemplateRow.push({
                    _id: null,
                    allowProductRotation: true,
                    productArrangement: null,
                    product: null,
                    shouldOrderProductIfNotInSite: false,
                    shouldSmoothProductTransition: true,
                    trayMaxCapacity: 0,
                });
            }
            newLocationTemplateTable.push(newLocationTemplateRow);
        }

        const selectedShelfTemplate = microstoreColumnTemplateFormValues.shelfTemplates[shelfIndex];
        const newShelfTemplates = [...microstoreColumnTemplateFormValues.shelfTemplates];
        newShelfTemplates[shelfIndex] = {
            ...selectedShelfTemplate,
            locationTemplateTable: newLocationTemplateTable,
        };
        setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
            newMicrostoreColumnTemplateFormValues: {
                ...microstoreColumnTemplateFormValues,
                shelfTemplates: newShelfTemplates,
            },
        });
    }

    function handleLocationClick(shelfIndex: number, rowIndex: number, columnIndex: number) {
        if (!validateCurrentLocationRequiredFields()) {
            return;
        }

        setSelectedShelfTemplateIndex(shelfIndex);
        setSelectedLocationTemplatePosition({
            column: columnIndex,
            row: rowIndex,
        });
    }

    function handleShelfClick(shelfIndex: number) {
        if (!validateCurrentLocationRequiredFields()) {
            return;
        }

        setSelectedShelfTemplateIndex(shelfIndex);
        setSelectedLocationTemplatePosition(null);
    }

    const numberOfShelvesOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8].map((numberOfShelves) => ({
        value: numberOfShelves,
        label: `${numberOfShelves} étagère${numberOfShelves > 1 ? 's' : ''}`,
    }));

    // Encode shelf configuration as "columns×rows" string to satisfy TotemSelect's OptionValue constraint
    const shelfConfigurationOptions = [1, 2, 3, 4, 5, 10].flatMap((numberOfColumn) =>
        [1, 2, 3, 4].map((numberOfRow) => ({
            value: `${numberOfColumn}x${numberOfRow}`,
            label: `${numberOfColumn} colonne${numberOfColumn > 1 ? 's' : ''} × ${numberOfRow} ligne${
                numberOfRow > 1 ? 's' : ''
            }`,
        })),
    );

    function parseShelfConfiguration(value: string): { numberOfColumn: number; numberOfRow: number } {
        const [numberOfColumn, numberOfRow] = value.split('x').map(Number);
        return { numberOfColumn, numberOfRow };
    }

    const selectedShelfTemplate =
        selectedShelfTemplateIndex !== null
            ? microstoreColumnTemplateFormValues.shelfTemplates[selectedShelfTemplateIndex]
            : null;

    const selectedShelfConfigurationOption = selectedShelfTemplate
        ? (shelfConfigurationOptions.find(
              (option) =>
                  option.value ===
                  `${selectedShelfTemplate.locationTemplateTable[0]?.length ?? 0}x${selectedShelfTemplate.locationTemplateTable.length}`,
          ) ?? null)
        : null;

    return (
        <Container>
            <TopBar>
                <TopBarLeft>
                    <TotemSelect<number>
                        placeholder="Nombre d'étagères"
                        value={microstoreColumnTemplateFormValues.shelfTemplates.length}
                        options={numberOfShelvesOptions}
                        onChange={(value) => {
                            if (value !== null) {
                                setShelvesOfColumn(value);
                            }
                        }}
                    />
                </TopBarLeft>
                <TopBarRight>
                    <TotemPrimaryButton type="button" $isSecondaryStyle onClick={() => setIsLegendOpen(true)}>
                        <FaQuestionCircle style={{ marginRight: '8px' }} />
                        Légende
                    </TotemPrimaryButton>
                </TopBarRight>
            </TopBar>

            <MainContent>
                <GridArea>
                    {selectedShelfTemplateIndex !== null &&
                    selectedLocationTemplatePosition === null &&
                    selectedShelfTemplate ? (
                        <ShelfConfigSection>
                            <TotemSelect<string>
                                label="Configuration de l'étagère sélectionnée"
                                placeholder="Choisir une configuration"
                                value={selectedShelfConfigurationOption?.value ?? null}
                                options={shelfConfigurationOptions}
                                onChange={(value) => {
                                    if (value) {
                                        const { numberOfColumn, numberOfRow } = parseShelfConfiguration(value);
                                        setLocationsOfShelf(selectedShelfTemplateIndex, numberOfColumn, numberOfRow);
                                    }
                                }}
                            />
                        </ShelfConfigSection>
                    ) : null}

                    <ShelvesContainer>
                        {microstoreColumnTemplateFormValues.shelfTemplates.map((shelf, shelfIndex) => (
                            <ShelfGrid
                                key={shelfIndex}
                                shelf={shelf}
                                shelfIndex={shelfIndex}
                                selectedShelfIndex={selectedShelfTemplateIndex}
                                selectedLocationPosition={selectedLocationTemplatePosition}
                                onLocationClick={handleLocationClick}
                                onShelfClick={handleShelfClick}
                            />
                        ))}
                    </ShelvesContainer>
                </GridArea>
            </MainContent>

            <LegendPanel isOpen={isLegendOpen} onClose={() => setIsLegendOpen(false)} />
        </Container>
    );
};

const Container = styled.div`
    min-height: calc(100vh - 200px);
    flex-grow: 1;
    display: flex;
    flex-direction: column;
`;

const TopBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
`;

const TopBarLeft = styled.div`
    flex: 1;
    max-width: 300px;
`;

const TopBarRight = styled.div`
    display: flex;
    gap: 8px;
`;

const MainContent = styled.div`
    position: relative;
    flex-grow: 1;
    display: flex;
    min-height: 0;
`;

const GridArea = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
`;

const ShelfConfigSection = styled.div`
    padding: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
`;

const ShelvesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
